/**
 * Stop a missed file drop from navigating the whole app away.
 *
 * The browser's default action for a file dropped on a page that does not
 * handle it is to *open that file* — the SPA unloads, and with it the
 * half-written incident, the uploads in flight and the conversation. The user's
 * mistake was missing a drop zone by twenty pixels; the punishment is losing
 * everything they typed. That trade is bad enough that the default has to go,
 * app-wide, for the whole time the app is mounted.
 *
 * Two rules keep it from becoming a nuisance in its own right.
 *
 * **Only files.** A drag carrying text or a URL is left completely alone.
 * Dragging a selection into the composer's textarea is a native editing gesture
 * that predates this app and has to keep working, and blanket-cancelling
 * `dragover` is exactly how a guard like this breaks it.
 *
 * **Only what nothing else claimed.** A real drop zone (see
 * `AttachmentUploader`) calls `preventDefault()` in its own `dragover` and
 * `drop` handlers, and `defaultPrevented` stays true for the rest of the
 * dispatch — so by the time the event bubbles up to `window`, an accepted drop
 * is already marked and is skipped here. React 19 attaches its listeners at the
 * root container, which is inside `window`, so component handlers always run
 * first and that ordering holds.
 *
 * `dragover` is suppressed as well as `drop`, for two reasons: the drop event
 * is only dispatched at all when the preceding `dragover` was cancelled, and
 * cancelling it is what lets `dropEffect` be set to `"none"` so the cursor says
 * "you can't drop that here" instead of promising something that will not
 * happen.
 */

import { useEffect } from "react";

import { dragCarriesFiles } from "@/components/attachments";
import { logger } from "@/lib/logger";

export function useFileDropGuard(): void {
  useEffect(() => {
    const suppress = (event: DragEvent) => {
      // A real drop zone already took it — leave it entirely alone.
      if (event.defaultPrevented || !dragCarriesFiles(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer !== null) {
        event.dataTransfer.dropEffect = "none";
      }
      if (event.type === "drop") {
        logger.debug("[attachments] file dropped outside a drop zone, ignored");
      }
    };

    window.addEventListener("dragover", suppress);
    window.addEventListener("drop", suppress);
    return () => {
      window.removeEventListener("dragover", suppress);
      window.removeEventListener("drop", suppress);
    };
  }, []);
}
